import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import { EquipmentHttpService, LeasingHttpService } from '@core/services/http';
import { useLeasingTabPrefetch } from '@core/utils/hooks';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { EditablePage, LeasingEquipment } from '@shared/components';
import { LeasingActions } from '@store/actions';
import {
  DDVSelectors,
  LeasingSelectors,
  ProjectSelectors,
  UserSelectors,
} from '@store/selectors';

export const LeasingEquipmentTab: FC<RouterProps> = () => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();

  const isLoading = useSelector(
    LeasingSelectors.getTabLoadingState('equipment'),
  );
  const isDataLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('equipment'),
  );

  const canEdit = useSelector(
    UserSelectors.canEdit('leasing', SubPage.LeasingBrowse, Tab.Equipment),
  );
  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );
  const { equipmentTypeNames, ...leasingEquipmentData } = useSelector(
    LeasingSelectors.getLeasingEquipmentData,
  );

  useLeasingTabPrefetch('equipment', projectBundleID);

  useEffect(() => {
    if (!isDataLoaded) {
      dispatch(
        LeasingActions.setTabLoading.done({
          tabName: 'equipment',
          isLoading: true,
        }),
      );
      HttpService.getHttpRequests(
        LeasingHttpService,
      ).getLeasingEquipmentTabData(projectBundleID);
    }
  }, [isDataLoaded]);

  useEffect(() => {
    if (equipmentTypeNames?.length) {
      HttpService.getHttpRequests(EquipmentHttpService).getEquipmentsByTypes(
        equipmentTypeNames,
      );
    }
  }, [equipmentTypeNames]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-leasing-equipment loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <EditablePage
      editableComponent={LeasingEquipment}
      canUserEdit={
        canEdit && canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
      }
      data={{ equipmentTypeNames, ...leasingEquipmentData }}
    />
  );
};
